/**
 * Migration-safe read of an hc-users payload while the backend flattens away the
 * legacy double `userData.userData` wrapper.
 *
 * The same record has, over time, lived at three nesting levels:
 *   - `payload.userData.userData` (legacy double-wrap)
 *   - `payload.userData`          (single-wrap / mid-migration)
 *   - `payload`                   (fully flattened, root)
 *
 * Reading any one fixed level breaks the moment the backend changes the shape.
 * These helpers probe all three and return whichever actually carries the data,
 * so the frontend works before, during, and after the flattening with no flag day.
 * Once the wrapper is gone everywhere, callers can be simplified to a plain root
 * read and this util retired.
 */

/** Candidate levels, outermost-first then deeper. */
function candidateLevels(payload: any): any[] {
  return [payload, payload?.userData, payload?.userData?.userData]
    .filter(c => c && typeof c === 'object');
}

/**
 * Returns the first candidate level that carries any of `markerKeys` (i.e. the
 * level that actually looks like the record you want). Use this when you know
 * which fields identify the payload — e.g. permissions = has `role`/`sites`/etc.
 * Returns null if no level carries a marker.
 */
export function pickUserDataLevel<T = any>(payload: any, markerKeys: string[]): T | null {
  const match = candidateLevels(payload)
    .find(level => markerKeys.some(k => level[k] !== undefined));
  return (match ?? null) as T | null;
}

/**
 * Returns the deepest present candidate level — peels however many `userData`
 * wrappers exist. Use this when the inner shape has dynamic keys you can't mark
 * on (e.g. saved-view payloads keyed by entity type). Returns null for an
 * empty/invalid payload.
 */
export function unwrapUserData<T = any>(payload: any): T | null {
  const levels = candidateLevels(payload);
  return (levels.length ? levels[levels.length - 1] : null) as T | null;
}

/**
 * Merges all present candidate levels into one object. Use this when a record's
 * fields may be SPLIT across levels mid-migration — e.g. the backend now writes
 * `role` at root while the permission arrays (sites / databaseOrganizations / etc.)
 * still live nested. Picking a single level would drop whichever fields live at the
 * other level; merging captures them all.
 *
 * Precedence: deeper levels are the base, shallower levels win — so a field freshly
 * written at root (role) overrides a stale nested copy, while fields that exist ONLY
 * nested (the arrays) are preserved (root doesn't carry them, so it can't clobber
 * them). Returns null for an empty/invalid payload.
 */
export function mergeUserDataLevels<T = any>(payload: any): T | null {
  const levels = candidateLevels(payload);
  if (!levels.length) return null;
  const deepestFirst = [...levels].reverse(); // [userData.userData, userData, root]
  return Object.assign({}, ...deepestFirst) as T;
}

/** Fields that identify a user permissions/record level. */
export const USER_RECORD_MARKERS = [
  'role', 'activated', 'sites', 'departments',
  'contractTypes', 'legalEntities', 'databaseOrganizations',
];
